/**
 * Feb 2027 · Global glossary quick-search (Ctrl+K / Cmd+K).
 *
 * A tiny centered dialog that pops from anywhere in the app so founders
 * can look up any of the 284 glossary terms without leaving their flow.
 * Result select navigates to `/glossary#slug`, which — thanks to the
 * deep-link + pulse work shipped alongside — scrolls the entry into
 * view and briefly highlights it.
 *
 * Behaviour:
 *   - Ctrl+K on Win/Linux, Cmd+K on macOS toggles the dialog. Also
 *     opens on the shared `?` shortcut? No — `?` is the shortcut legend.
 *   - Fires unlike the other shortcuts EVEN inside input fields, since
 *     the whole point of Cmd+K is to work mid-typing (Notion / Linear /
 *     Slack precedent). Ctrl+K is rarely used by web forms.
 *   - Escape closes. Arrow up/down navigates. Enter selects.
 *   - Input auto-focuses on open; auto-clears on close.
 *   - Emits `plan_shortcut_used` telemetry on open (id, page="global") +
 *     `glossary_quick_search_select` on result select so we can see
 *     which terms drive the most look-ups.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, BookOpen, ArrowRight } from "lucide-react";
import useGlossary from "../lib/useGlossary";
import { trackEvent } from "../lib/telemetry";

// Small helper — turns "Cash flow" into "cash-flow" so URLs match the
// same slug scheme the GlossaryPage deep-link resolver uses.
function toSlug(term) {
  return String(term || "").toLowerCase().replace(/\s+/g, "-");
}

// Rank a term against the query: exact prefix > term-contains > alias
// contains > definition contains. Cheap Array.sort keeps this snappy
// even at 284 entries because JS strings compare in O(1) for short
// strings and we cap at the first 8 results anyway.
function score(entry, needle) {
  const term = entry.term.toLowerCase();
  const aliases = (entry.aliases || []).map((a) => a.toLowerCase());
  if (term.startsWith(needle)) return 0;
  if (aliases.some((a) => a.startsWith(needle))) return 1;
  if (term.includes(needle)) return 2;
  if (aliases.some((a) => a.includes(needle))) return 3;
  if ((entry.definition || "").toLowerCase().includes(needle)) return 4;
  return 999;
}

const MAX_RESULTS = 8;

export default function GlossaryQuickSearch() {
  const data = useGlossary();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Global Ctrl+K / Cmd+K keydown listener.
  useEffect(() => {
    const handler = (e) => {
      const cmdK =
        (e.metaKey || e.ctrlKey) &&
        !e.shiftKey &&
        !e.altKey &&
        (e.key === "k" || e.key === "K");
      if (!cmdK) return;
      e.preventDefault();
      setOpen((prev) => {
        const next = !prev;
        if (next) {
          try {
            trackEvent("plan_shortcut_used", { id: "quick_glossary_search", page: "global" });
          } catch { /* silent */ }
        }
        return next;
      });
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Autofocus + reset when opening; clear on close.
  useEffect(() => {
    if (open) {
      setQ("");
      setActiveIdx(0);
      // Wait one paint so the input exists in the DOM.
      const t = window.setTimeout(() => inputRef.current?.focus(), 20);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [open]);

  const results = useMemo(() => {
    if (!data || !q.trim()) return [];
    const needle = q.trim().toLowerCase();
    return (data.terms || [])
      .map((t) => ({ entry: t, s: score(t, needle) }))
      .filter((r) => r.s < 999)
      .sort((a, b) => a.s - b.s || a.entry.term.localeCompare(b.entry.term))
      .slice(0, MAX_RESULTS)
      .map((r) => r.entry);
  }, [data, q]);

  const close = useCallback(() => setOpen(false), []);

  const selectAt = useCallback(
    (idx) => {
      const entry = results[idx];
      if (!entry) return;
      const slug = toSlug(entry.term);
      try {
        trackEvent("glossary_quick_search_select", { slug, query: q });
      } catch { /* silent */ }
      setOpen(false);
      navigate(`/glossary#${slug}`);
    },
    [results, navigate, q],
  );

  // Keyboard navigation inside the dialog.
  const onKey = useCallback(
    (e) => {
      if (!open) return;
      if (e.key === "Escape") {
        e.preventDefault();
        close();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => Math.min(results.length - 1, i + 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => Math.max(0, i - 1));
      } else if (e.key === "Enter") {
        e.preventDefault();
        selectAt(activeIdx);
      }
    },
    [open, close, results.length, selectAt, activeIdx],
  );

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-start justify-center pt-[12vh] px-4"
      data-testid="glossary-quick-search"
      role="dialog"
      aria-label="Glossary quick search"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-neutral-900/40 backdrop-blur-[2px]"
        onClick={close}
        aria-hidden="true"
        data-testid="glossary-quick-search-backdrop"
      />
      {/* Panel */}
      <div
        className="relative w-full max-w-xl bg-white border-2 border-neutral-900 shadow-2xl"
        onKeyDown={onKey}
        data-testid="glossary-quick-search-panel"
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-neutral-200">
          <Search className="w-4 h-4 text-neutral-500 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setActiveIdx(0);
            }}
            placeholder="Look up any glossary term…"
            data-testid="glossary-quick-search-input"
            className="flex-1 outline-none text-sm placeholder:text-neutral-400"
          />
          <kbd
            className="text-[10px] font-mono font-bold border border-neutral-300 bg-neutral-50 px-1.5 py-0.5 rounded"
          >
            ESC
          </kbd>
        </div>

        {!q.trim() && (
          <div
            className="px-4 py-8 text-center text-xs text-neutral-500"
            data-testid="glossary-quick-search-hint"
          >
            <BookOpen className="w-5 h-5 mx-auto mb-2 text-neutral-400" />
            Type any term — LLC, CAC, cash flow, runway — to jump to its
            plain-English definition.
          </div>
        )}

        {q.trim() && results.length === 0 && (
          <div
            className="px-4 py-8 text-center text-xs text-neutral-500"
            data-testid="glossary-quick-search-empty"
          >
            No terms match &quot;<b>{q}</b>&quot;. The full glossary has {data?.terms?.length || 0} entries.
          </div>
        )}

        {results.length > 0 && (
          <ul
            className="max-h-[50vh] overflow-y-auto"
            data-testid="glossary-quick-search-results"
          >
            {results.map((r, i) => {
              const slug = toSlug(r.term);
              const active = i === activeIdx;
              return (
                <li
                  key={r.term}
                  data-testid={`glossary-quick-search-result-${slug}`}
                  data-active={active ? "on" : undefined}
                  onMouseEnter={() => setActiveIdx(i)}
                  onClick={() => selectAt(i)}
                  className={
                    "px-4 py-3 border-b border-neutral-100 last:border-0 cursor-pointer flex items-start gap-3 " +
                    (active ? "bg-[#F5F0FB]" : "hover:bg-neutral-50")
                  }
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-neutral-900 text-sm truncate">
                      {r.term}
                      {r.aliases?.length > 0 && (
                        <span className="ml-2 text-[10px] uppercase tracking-[0.12em] text-neutral-400 font-normal">
                          {r.aliases.join(" · ")}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-neutral-600 mt-0.5 line-clamp-2">
                      {r.definition}
                    </div>
                  </div>
                  <ArrowRight className="w-3.5 h-3.5 text-neutral-400 shrink-0 mt-1" />
                </li>
              );
            })}
          </ul>
        )}

        <div className="flex items-center justify-between px-4 py-2 border-t border-neutral-200 text-[10px] uppercase tracking-[0.15em] text-neutral-500">
          <span>
            <kbd className="font-mono font-bold text-neutral-700">↑ ↓</kbd> navigate ·{" "}
            <kbd className="font-mono font-bold text-neutral-700">↵</kbd> open
          </span>
          <span>{results.length} of {data?.terms?.length || 0}</span>
        </div>
      </div>
    </div>
  );
}
